import React, { useEffect, useContext } from "react";
import styled from "styled-components";
import {
  Modal,
  Button,
  Typography,
  Icon,
  COLORS,
} from "@virkesborsen/ui-component-library";
import { Navigation } from "../../context";
import { useCheckoutData } from "./hooks/useCheckoutData";
import { useCheckoutSteps } from "./hooks/useCheckoutSteps";
import { trackCheckoutEvent } from "./utils/analytics";
import { sendEvent } from "../../../../api";
import ProductSelectionStep from "./steps/productSelectionStep";
import PaymentSelectionStep from "./steps/paymentSelectionStep";
import ThankYouStep from "./steps/thankYouStep";
import { BackButton, CloseButton } from "../DialogNavigationButtons";
import { formatPriceWithCurrency } from "../../utils";

const STEPS = [ProductSelectionStep, PaymentSelectionStep, ThankYouStep];

const DFRCheckoutDialog = ({
  visible,
  hide,
  initialStep,
  couponCode,
  setCouponCode,
}) => {
  if (!visible) return null;

  const navContext = useContext(Navigation);
  const {
    checkoutData,
    updateCheckoutData,
    error,
    clearCheckoutData,
    fetchPrices,
    isLoading,
    isError,
    isLargeProperty,
    hasValidData,
    canProceedToPayment,

    netPrice,
    basePrice,
  } = useCheckoutData();

  const {
    activeStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    resetSteps,
    shouldSkipPayment,
    stepNames,
    isLastStep,
  } = useCheckoutSteps(initialStep);

  useEffect(() => {
    if (visible && activeStep === 0) {
      sendEvent("dfr_open_modal", { source: "dfr" });
    }
  }, [visible, activeStep]);

  useEffect(() => {
    if (couponCode) {
      updateCheckoutData({ couponCode });
    }
  }, [couponCode]);

  useEffect(() => {
    if (visible) {
      trackCheckoutEvent("step_viewed", {
        step: stepNames[activeStep],
        step_index: activeStep + 1,
      });
    }
  }, []);

  const handleClose = () => {
    trackCheckoutEvent("closed", {
      step: stepNames[activeStep],
      step_index: activeStep + 1,
    });
    resetSteps();
    hide();
  };

  const handleNext = () => {
    if (isLastStep) {
      redirectToDRF();
      clearCheckoutData();
      hide();
      return;
    }

    goToNextStep();
  };

  const handleSkipPayment = () => {
    if (shouldSkipPayment({ isLargeProperty })) {
      goToStep(2);
    } else {
      goToNextStep();
    }
  };

  const getNavigationButtonLabel = () => {
    if (StepComponent.displayName === "ThankYou") {
      return isLargeProperty ? "Stäng" : "Gå till Skogsövervakning";
    }
    return "Slutför beställning";
  };

  const redirectToDRF = () => {
    const view = { comp: "overvakning" };
    history.pushState(view, "", "");
    navContext.setView(view);
  };

  const StepComponent = STEPS[activeStep];
  const stepProps = {
    checkoutData,
    updateCheckoutData,
    error,

    couponCode,
    setCouponCode,
    skipKlarnaCheckout: handleSkipPayment,
    fetchPrices,
    isLoading,
    isError,
    isLargeProperty,
    hasValidData,
  };

  const isPaymentStep = StepComponent.displayName === "PaymentSelection";
  const hideBackButton = ["ProductSelection", "ThankYou"].includes(
    StepComponent.displayName
  );

  const navigationButtonLabel = getNavigationButtonLabel();

  const isButtonDisabled =
    isLoading || (activeStep === 0 && !canProceedToPayment);

  return (
    <Modal isOpen={visible} borderLess onClose={handleClose}>
      <Modal.Header>
        <BackButton handleBack={goToPreviousStep} hide={hideBackButton} />
        <div className="d-flex w-100 align-items-center gap-2 justify-content-center">
          <IconWrapper>
            <Icon
              name="SatelliteSimple"
              size="large"
              color="spring-yellow-500"
            />
          </IconWrapper>
          <div className="d-flex flex-column gap-1">
            <Typography variant="labelMediumMediumDense">
              Skogsövervakning
            </Typography>
          </div>
        </div>
        <CloseButton handleClose={handleClose} />
      </Modal.Header>

      <StepComponent {...stepProps} />

      {!isPaymentStep && (
        <Modal.Footer>
          {!isLargeProperty && (
            <div className="d-flex justify-content-between w-100">
              <>
                <Typography
                  variant="bodyMediumBaseRegular"
                  color="charcoal-500"
                >
                  Ditt pris
                </Typography>
                <div className="d-flex gap-1">
                  {/* {basePrice && (
                    <Typography
                      variant="labelLargeMedium"
                      color="charcoal-200"
                      className="text-decoration-line-through"
                    >
                      {formatPriceWithCurrency(basePrice)}
                    </Typography>
                  )} */}
                  <Typography variant="labelLargeMedium" color="charcoal-500">
                    {formatPriceWithCurrency(netPrice)}
                  </Typography>
                </div>
              </>
            </div>
          )}
          <Button
            fullWidth
            variant="primary"
            onClick={handleNext}
            label={isLoading ? "Hämtar pris..." : navigationButtonLabel}
            suffixIcon={isLoading ? undefined : "ArrowRightSimple"}
            disabled={isButtonDisabled}
          />
        </Modal.Footer>
      )}
    </Modal>
  );
};

const IconWrapper = styled.div`
  width: 36px;
  height: 36px;
  background-color: ${COLORS["charcoal-500"]};
  border-radius: 8px;
  padding: 8px;
`;

export default DFRCheckoutDialog;
